import { TokenResponse } from 'auth0'
import axios, { AxiosError } from 'axios'
import { Auth0Error, LOGIN_ERRORS } from 'modules/auth/types/auth0'
import { useTrackEvent } from 'modules/common/hooks/useTrackEvent'
import { EnumCorporateQueryKeys } from 'modules/corporate/queries/keys'
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { buildLoginScope } from '../lib/auth0/scope'
import { clearAccessToken } from '../lib/auth0/token'
import { AUTH_TRACKING_EVENTS } from '../lib/tracking/events'
import { EnumAuthQueryKeys } from '../queries/keys'

interface MutationVariables {
  email: string
  password: string
}

const loginUser = async (user: MutationVariables) => {
  const scope = buildLoginScope()

  const { data } = await axios.post<TokenResponse>('/api/auth/login', {
    ...user,
    scope,
  })

  return data
}

export const useLoginUserMutation = (
  mutationOptions?: UseMutationOptions<
    TokenResponse,
    AxiosError<Auth0Error>,
    MutationVariables
  >
) => {
  const queryClient = useQueryClient()
  const trackEvent = useTrackEvent()

  return useMutation<TokenResponse, AxiosError<Auth0Error>, MutationVariables>(
    async (args) => loginUser(args),
    {
      ...mutationOptions,
      onMutate: (variables) => {
        trackEvent({
          eventType: AUTH_TRACKING_EVENTS.LOGIN,
        })
        mutationOptions?.onMutate?.(variables)
      },
      onSuccess: (data, variables, context) => {
        queryClient.resetQueries(EnumAuthQueryKeys.AUTHENTICATED_USER)
        queryClient.resetQueries(EnumCorporateQueryKeys.CORPORATES_STATUS)

        trackEvent({
          eventType: AUTH_TRACKING_EVENTS.LOGIN_SUCCESS,
        })

        mutationOptions?.onSuccess?.(data, variables, context)
      },
      onError: (err, variables, context) => {
        clearAccessToken()

        if (err?.response?.data?.error === LOGIN_ERRORS.TOO_MANY_ATTEMPTS) {
          trackEvent({
            eventType: AUTH_TRACKING_EVENTS.LOGIN_FAILED_TOO_MANY_ATTEMPS,
          })
        } else if (err?.response?.data?.error === LOGIN_ERRORS.INVALID_GRANT) {
          trackEvent({
            eventType: AUTH_TRACKING_EVENTS.LOGIN_FAILED_INVALID_CREDENTIALS,
          })
        } else {
          trackEvent({
            eventType: AUTH_TRACKING_EVENTS.LOGIN_FAILED_TECHNICAL_ERROR,
          })
        }

        queryClient.invalidateQueries(EnumAuthQueryKeys.AUTHENTICATED_USER)

        mutationOptions?.onError?.(err, variables, context)
      },
    }
  )
}
